import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Touch-friendly numeric keypad shown as a bottom sheet. Used for entering
 * discount values, weights, manual amounts — anywhere a soft keyboard would
 * be awkward on a cashier tablet/POS screen.
 *
 * Self-contained: caller passes the current numeric value (or empty) and a
 * confirm callback receives the parsed number. Min/max are clamped here so
 * callers don't have to re-validate.
 */
export interface NumpadModalProps {
  open: boolean;
  /** Initial value displayed when the keypad opens. 0 / null / undefined → empty. */
  initialValue?: number | null;
  /** Symbol shown after the number — usually '%' for percentages or '€' for amounts. */
  suffix?: string;
  /** When true the decimal point key is enabled (e.g. for € amounts). Default false. */
  allowDecimal?: boolean;
  /** Optional header (e.g. "Έκπτωση παραγγελίας"). */
  title?: string;
  /** Lower clamp applied to the confirmed value. Default 0. */
  min?: number;
  /** Upper clamp applied to the confirmed value. Default Infinity. */
  max?: number;
  onClose: () => void;
  onConfirm: (value: number) => void;
}

const NumpadModal: React.FC<NumpadModalProps> = ({
  open,
  initialValue = 0,
  suffix = '%',
  allowDecimal = false,
  title,
  min = 0,
  max = Number.POSITIVE_INFINITY,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation(['common']);
  const [value, setValue] = useState<string>('');

  // Re-prime the buffer each time the keypad opens. We keep the buffer empty
  // when the initial value is 0 so the cashier can start typing immediately
  // without first hitting backspace.
  useEffect(() => {
    if (!open) return;
    setValue(initialValue && initialValue > 0 ? String(initialValue) : '');
  }, [open, initialValue]);

  if (!open) return null;

  const handleKey = (key: string) => {
    setValue(prev => {
      if (key === '⌫') return prev.slice(0, -1);
      if (key === '.') {
        if (!allowDecimal) return prev;
        if (prev.includes('.')) return prev;
        return prev === '' ? '0.' : prev + '.';
      }
      // Limit to 6 chars to keep the display readable (covers 999.99 / 100%).
      if (prev.length >= 6) return prev;
      return prev + key;
    });
  };

  const handleConfirm = () => {
    const parsed = parseFloat(value) || 0;
    const clamped = Math.max(min, Math.min(max, parsed));
    onConfirm(clamped);
  };

  return (
    <div
      className="cashier-overlay fixed inset-0 flex items-end justify-center z-[70]"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={e => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        className="cashier-dialog bg-surface-1 rounded-t-2xl p-5 w-full max-w-sm border-t border-line-strong shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {title && (
          <h3 className="text-center text-base font-semibold text-t2 mb-3">{title}</h3>
        )}
        {/* Display */}
        <div className="text-center text-4xl font-bold text-t1 mb-4 py-3 px-4 bg-surface-2 rounded-xl tracking-wider min-h-[64px] flex items-center justify-center gap-1">
          <span>{value || '0'}</span>
          <span className="text-t3 text-2xl">{suffix}</span>
        </div>
        {/* Keys */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9',
            allowDecimal ? '.' : '',
            '0', '⌫',
          ].map((key, i) => (
            <button
              key={i}
              type="button"
              disabled={key === ''}
              onClick={() => key && handleKey(key)}
              className={`h-16 rounded-xl text-2xl font-bold transition-all active:scale-90 ${
                key === '⌫'
                  ? 'bg-red-100 dark:bg-red-900/60 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800/60'
                  : key === ''
                  ? 'opacity-0 pointer-events-none'
                  : 'bg-surface-3 text-t1 hover:bg-surface-4'
              }`}
            >
              {key}
            </button>
          ))}
        </div>
        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-14 rounded-xl border-2 border-line-strong text-t3 font-bold hover:bg-surface-2 active:scale-[0.97] transition-all"
          >
            {t('common:cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="cashier-primary flex-1 h-14 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg active:scale-[0.97] transition-all"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default NumpadModal;
